import { DocumentResults } from 'src/types/Document';
import { ModalType } from '../../../types/Modal';
import ModalComponent from './index';
// @ts-expect-error not
import CanvasJSReact from '@canvasjs/react-charts';
import { Card, CardContent } from '@mui/material';
import MarkdownPreview from '@uiw/react-markdown-preview';
import { AnimatePresence, motion } from 'framer-motion';
import {
    BarChart2,
    ChevronLeft,
    ChevronRight,
    FileIcon,
    FileQuestion,
    FileText,
    Frown,
    Meh,
    PieChartIcon,
    Smile,
} from 'lucide-react';
import * as React from 'react';
import { Fragment, useMemo, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useTranslate } from 'sbx-crm-menu-ui';
import { CardTitle } from 'src/components/ui/card';
import { Textarea } from 'src/components/ui/textarea';
import { AppAgentContext } from 'src/context/ContextProvider';
import { askQuestionToDocumentService } from 'src/services/DocumentService';
import { capitalize, grantPermission } from 'src/utils';
import useAsyncEffect from '../../../hooks/useAsyncEffect';
import { downloadFile, getFolderFilesService } from '../../../services/AssetService';
import { ScrollArea } from '../../ui/scroll-area';
import ButtonComponent from '../ButtonComponent';
import TableComponent, { KeyHeader } from '../TableComponent';
import { TabsComponent } from '../TabsComponent';
import { DocumentV2Response } from 'src/types/DocumentOrImageV2';
import { SourcesAnswerComponent } from '../SourcesAnswerComponent';
import { DOCUMENT_V2_CREATE } from 'src/types/Permission';

export interface DocumentResultsProps {
    title: string;
    onClose: () => void;
    data: DocumentResults | null;
}

export type DocumentResultsPropsWrapper = { type: ModalType.DOCUMENT_RESULTS, data: DocumentResultsProps };

const CanvasJSChart = CanvasJSReact.CanvasJSChart;
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const tabVariants = {
    inactive: {
        backgroundColor: '#ffffff',
        color: '#6b21a8',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    active: {
        backgroundColor: '#8b5cf6',
        color: '#ffffff',
        boxShadow: '0 4px 6px rgba(139,92,246,0.5)',
    },
    disabled: {
        backgroundColor: '#e5e7eb',
        color: '#9ca3af',
        boxShadow: 'none',
        cursor: 'not-allowed',
    },
};

const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: 300,
            damping: 30,
        },
    },
};

interface Sentiment {
    name: string;
    value: number;
    color: string;
    icon: any;
}


const SentimentFlower: React.FC<{ sentiments: Sentiment[] }> = ({ sentiments }) => {
    const [hoveredPetal, setHoveredPetal] = useState<string | null>(null);
    const total = sentiments.reduce((sum, sentiment) => sum + sentiment.value, 0);

    const createPetalPath = (startAngle: number, endAngle: number, innerRadius: number, outerRadius: number) => {
        const start = polarToCartesian(50, 50, outerRadius, endAngle);
        const end = polarToCartesian(50, 50, outerRadius, startAngle);
        const innerStart = polarToCartesian(50, 50, innerRadius, endAngle);
        const innerEnd = polarToCartesian(50, 50, innerRadius, startAngle);
        const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

        return [
            'M', start.x, start.y,
            'A', outerRadius, outerRadius, 0, largeArcFlag, 0, end.x, end.y,
            'L', innerEnd.x, innerEnd.y,
            'A', innerRadius, innerRadius, 0, largeArcFlag, 1, innerStart.x, innerStart.y,
            'Z',
        ].join(' ');
    };

    const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
        const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
        return {
            x: centerX + (radius * Math.cos(angleInRadians)),
            y: centerY + (radius * Math.sin(angleInRadians)),
        };
    };

    let startAngle = 0;

    return (
        <div className="relative w-80 h-80">
            <svg viewBox="0 0 100 100" className="w-full h-full">
                {sentiments.map((sentiment, index) => {
                    const angle = (sentiment.value / total) * 360;
                    const endAngle = startAngle + angle;
                    const path = createPetalPath(startAngle, endAngle, 20, 48);

                    const petal = (
                        <motion.path
                            key={sentiment.name}
                            d={path}
                            fill={sentiment.color}
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, delay: index * 0.2 }}
                            onMouseEnter={() => setHoveredPetal(sentiment.name)}
                            onMouseLeave={() => setHoveredPetal(null)}
                            whileHover={{ scale: 1.05 }}
                        />
                    );

                    startAngle = endAngle;
                    return petal;
                })}
                <circle cx="50" cy="50" r="20" fill="white" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <AnimatePresence>
                    {hoveredPetal ? (
                        <motion.div
                            key={hoveredPetal}
                            className="text-center bg-white bg-opacity-80 p-2 rounded-lg shadow-lg"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className="text-3xl font-bold"
                                 style={{ color: sentiments.find(s => s.name === hoveredPetal)?.color }}>
                                {sentiments.find(s => s.name === hoveredPetal)?.value}%
                            </div>
                            <div className="text-sm text-gray-600">{hoveredPetal}</div>
                        </motion.div>
                    ) : (
                        <motion.div
                            className="text-center"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                        >
                            <div className="text-4xl font-bold text-purple-600">{Math.round(total)}%</div>
                            <div className="text-sm text-gray-600">Total</div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

const emptyDataDocument = {
    message: "",
    sources: [
        {
            metadata: {
                source: undefined,
                page: undefined
            },
            page_content: ""
        }
    ]
};

export const DocumentResultsModal = ({ title, onClose, data }: DocumentResultsProps) => {
    const { state:{ session: { user_id, permissions } } } = React.useContext(AppAgentContext);
    const { t: agent } = useTranslate("agent");
    const [numPages, setNumPages] = useState<number>(1);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [question, setQuestion] = useState<string>('');
    const [documentAnswer, setDocumentAnswer] = useState<DocumentV2Response>(emptyDataDocument);
    const [loading, setLoading] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState('sentiment_analysis_resume');

    const { t: documentT } = useTranslate('document');
    const { t: common } = useTranslate('common');

    const allowCreate = useMemo(() => {
        return grantPermission(DOCUMENT_V2_CREATE, permissions);
    }, [permissions]);

    function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
        setNumPages(numPages);
    }

    const [document, setDocument] = useState({
        url: '',
        name: '',
    });

    useAsyncEffect(async () => {
        // console.log('data', data);
        if (data?.source) {
            const response = await getFolderFilesService(data.source);
            if (response?.success && response.items && response.items[0]) {
                const file = response.items[0];
                const responseFile = await downloadFile(file.key);
                if (responseFile?.success && responseFile.item) {
                    setDocument({ url: responseFile.item.url, name: responseFile.item.name });
                }
            }
        }
    }, [data]);

    const handleNextPage = () => {
        if (pageNumber < numPages) {
            setPageNumber(pageNumber + 1);
        }
    };

    const handlePreviousPage = () => {
        if (pageNumber > 1) {
            setPageNumber(pageNumber - 1);
        }
    };

    const tabs = useMemo(() => [
        {
            id: 'sentiment_analysis_resume',
            icon: <PieChartIcon className="inline-block mr-2 h-5 w-5" />,
            label: documentT('sentiment_analysis_resume'),
            color: 'from-purple-500 to-pink-500',
        },
        {
            id: 'frequency',
            icon: <BarChart2 className="inline-block mr-2 h-5 w-5" />,
            label: documentT('word_frequency'),
            color: 'from-blue-500 to-cyan-500',
        },
        {
            id: 'summary',
            icon: <FileText className="inline-block mr-2 h-5 w-5" />,
            label: documentT('document_analysis'),
            color: 'from-green-500 to-teal-500',
        },
        {
            id: 'sentiment_analysis',
            icon: <Smile className="inline-block mr-2 h-5 w-5" />,
            label: documentT("risk_analysis_score"),
            color: 'from-red-500 to-yellow-500',
        },
        {
            id: 'document',
            icon: <FileIcon className="inline-block mr-2 h-5 w-5" />,
            label: <span className="text-wrap text-center">
                {`${documentT('document')} ${document?.name || ''}`}
            </span>,
            disabled: document.url.length === 0,
            color: 'from-yellow-500 to-orange-500',
        },
    ], [document]);

    const getColorBySentiment = (feeling: string) => {
        if (['positive', 'positivo'].includes(feeling.toLowerCase())) {
            return '#22c55e';
        }

        if (['neutral'].includes(feeling)) {
            return '#fbbf24';
        }

        if (['negative', 'negativo'].includes(feeling.toLowerCase())) {
            return '#ef4444';
        }

        return '';
    };

    const getIconBySentiment = (feeling: string) => {
        if (['positive', 'positivo'].includes(feeling.toLowerCase())) {
            return <Smile className="w-8 h-8 mb-2" style={{ color: getColorBySentiment(feeling) }} />;
        }

        if (['neutral'].includes(feeling)) {
            return <Meh className="w-8 h-8 mb-2" style={{ color: getColorBySentiment(feeling) }} />;
        }

        if (['negative', 'negativo'].includes(feeling.toLowerCase())) {
            return <Frown className="w-8 h-8 mb-2" style={{ color: getColorBySentiment(feeling) }} />;
        }

        return null;
    };

    const questionToAgent = async() => {
        setLoading(true);
        setDocumentAnswer(emptyDataDocument);
        // let i = 0;
        const res = await askQuestionToDocumentService({
            agent_id: data?.agent || 0,
            user: user_id,
            question: `${question} \n (Important Note: Always Return your answer on a simple beautifully markdown format)`,
            test: false,
            history: false,
            key_filter: 'document_analysis',
            session_id: new Date().getTime().toString(),
            meta_data: {
                folder: data?.source || ''
            }
        });

        if (res?.success) {
            setLoading(false);
            setDocumentAnswer(res.item);
            // const interval = setInterval(async() => {
            //     if(i === 20) {
            //         clearInterval(interval);
            //         setLoading(false);
            //     }
                    
            //     const data = await getAllJobsService({ user: user_id, job_type: 'Chat', key_filter: 'document_analysis'});
            //     if(data.success) {
            //         const item = data.items[data.items.length - 1];
            //         const message: string = item.getResults('string');
            //         if(message?.length > 0) {
            //             setAnswer(message);
            //             setLoading(false);
            //             clearInterval(interval);
            //         }
            //         i++;
            //     }
            // }, 4000);
        } else {
            setLoading(false);
        }
    }

    const AnswerDocument = () => {
        return (
            <div>
                {/* <CardTitle className="text-lg font-semibold text-purple-700 mb-2">{agent('answer')}</CardTitle> */}
                <Card className="bg-gradient-to-r from-purple-50 to-pink-50">
                    <CardContent className="p-0">
                        <div
                            className={`${documentAnswer.message.length > 0 ? 'max-h-[80vh]' : 'h-32'}  w-full rounded-md overflow-auto`}>
                            {documentAnswer.message ?
                                <div className="">
                                    <MarkdownPreview className={''}
                                                    style={{ padding: 16 }}
                                                    source={documentAnswer.message.replaceAll("```markdown\n", "").replaceAll("```", "")} />
                                </div> :
                                    <p className="text-gray-400 p-4">
                                        {loading ? `${agent('processing')}...` : agent('empty_answer')}
                                    </p>}
                        </div>
                    </CardContent>
                </Card>
            </div>
        )
    }


    const renderContent = (state: DocumentResults | null) => {

        const data = state?.sentiment_analysis_resume ? Object.entries(state.sentiment_analysis_resume).map(([key, value]) => ({
            name: capitalize(key),
            value: Math.round(value* 100) ,
            icon: getIconBySentiment(key),
            color: getColorBySentiment(key),
        } as Sentiment)) : [];

        switch (activeTab) {
            case 'sentiment_analysis_resume':
                return state?.sentiment_analysis_resume ? (
                    <>
                        <h1 className="text-3xl text-center font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                            {documentT('sentiment_analysis_resume')}
                        </h1>


                        <div className="flex flex-col lg:flex-row items-center justify-center space-x-6">
                            <SentimentFlower sentiments={data} />
                            <div className="grid grid-cols-3 gap-4 w-full max-w-md">
                                {data.map((sentiment, index) => (
                                    <motion.div
                                        key={sentiment.name}
                                        className="flex flex-col items-center p-3 rounded-lg shadow-md"
                                        style={{ backgroundColor: `${sentiment.color}20` }}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: index * 0.1 }}
                                    >
                                        {sentiment.icon}
                                        <span className="font-medium text-gray-700 text-center">{sentiment.name}</span>
                                        <span className="font-semibold text-2xl"
                                              style={{ color: sentiment.color }}>{sentiment.value}%</span>
                                    </motion.div>
                                ))}
                            </div>
                        </div>


                    </>
                ) : null;

            case 'frequency':
                return state?.word_frequency ? (
                    <>
                        <h1 className="text-3xl text-center font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                            {documentT('word_frequency')}
                        </h1>
                        <div className="flex justify-center py-4">
                            <CanvasJSChart
                                containerProps={{ width: '95%', height: 350 }}
                                options={{
                                    animationEnabled: true,
                                    theme: 'light2',
                                    title: {
                                        // text: 'Frecuencia de palabras',
                                    },
                                    data: [{
                                        type: 'bar',
                                        dataPoints: Object.entries(state?.word_frequency).map(([key, value]) => ({
                                            label: key, y: value,
                                        })),
                                    }],
                                }}
                            />
                        </div>
                    </>
                ) : null;
            case 'summary':
                return state?.llm_analysis && (
                    <>
                        <h1 className="text-3xl text-center font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                            {documentT('document_analysis')}
                        </h1>
                        <div className=" py-4 px-4">
                            <MarkdownPreview source={state?.llm_analysis} style={{ padding: 16 }} />
                        </div>
                    </>
                );
            case 'sentiment_analysis':
                return state?.sentiment_analysis && (
                    <>
                        <h1 className="text-3xl text-center font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                            {documentT("risk_analysis_score")}
                        </h1>
                        <div className=" py-4 px-2">
                            <TableComponent
                                items={state.sentiment_analysis.map(item => {
                                    return {
                                        polarity: item.polarity,
                                        text: item.text.split('\n').map((line, index) => (
                                            <Fragment key={index}>
                                                {line}
                                                <br />
                                            </Fragment>
                                        )),
                                    };
                                })}
                                pagination
                                header={[
                                    {
                                        type: KeyHeader.KEY,
                                        label: 'Puntaje',
                                        key: 'polarity',
                                    },
                                    {
                                        type: KeyHeader.KEY,
                                        label: 'Respuesta',
                                        key: 'text',
                                    },
                                ]}
                            />
                        </div>
                    </>
                );
            case 'document':
                return <div>
                    {/*<h1 className="text-3xl text-center font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">*/}
                    {/*    {document.name}*/}
                    {/*</h1>*/}
                    <div className='bg-white shadow-lg rounded-2xl mb-3 p-6 grid grid-cols-1 lg:grid-cols-2 gap-6'>
                        <div>
                            <CardTitle
                                className="text-lg font-semibold text-purple-700 mb-4">{agent('ask_a_question')}</CardTitle>
                            <Textarea
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                disabled={!allowCreate}
                                placeholder={agent('ask_a_question')}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        // questionToAgent();
                                    }
                                }}
                                className="w-full border-2 border-purple-300 focus:border-purple-500 rounded-xl py-2 px-4 resize-none"
                                rows={5}
                            />
                            <div className='flex justify-end'>
                                <ButtonComponent
                                    loading={loading}
                                    disabled={question.trim().length === 0 || !allowCreate}
                                    onClick={questionToAgent}
                                    label={<>
                                        <FileQuestion size={20} className="mr-2" /> {agent('ask_a_question')}
                                    </>}
                                    className="mt-5 primary-gradient-button text-white font-semibold py-2 px-4 rounded-full"
                                />
                            </div>
                        </div>
                        <div>
                            <TabsComponent
                                colorPrimary='rgb(126 34 206)'
                                tabs={[
                                    {
                                        label: agent('answer'),
                                        disabled: false,
                                        component: <AnswerDocument />
                                    },
                                    {
                                        label: agent('sources'),
                                        disabled: false,
                                        component: <SourcesAnswerComponent 
                                        numPages={documentAnswer.sources.length} 
                                        document={documentAnswer}
                                        loading={loading}
                                        />
                                    }
                                ]}
                            />
                        </div>
                    </div>
                    <div className='bg-white shadow-lg rounded-2xl p-6'>
                        <div className="flex items-center gap-1 justify-center">
                            <ChevronLeft onClick={handlePreviousPage} className="cursor-pointer" />
                            <p>
                                {documentT("page")} {pageNumber} {common("of")} {numPages}
                            </p>
                            <ChevronRight onClick={handleNextPage} className="cursor-pointer" />
                        </div>

                        <Document file={document.url} className="flex justify-center"
                                onLoadSuccess={onDocumentLoadSuccess}>
                            <Page pageNumber={pageNumber} />
                        </Document>
                    </div>
                </div>;
            default:
                return null;
        }
    };


    return (
        <ModalComponent
            onClose={onClose}
            className="bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100 rounded-3xl shadow-2xl max-w-[95vw]"
            title={title}
            open
            state={data}
        >
            {(state) => {

                return (
                    <div>
                        <div
                            className="flex  flex-col md:flex-row flex-nowrap md:flex-wrap justify-start md:justify-center gap-y-2 lg:gap-2 pb-2 ">
                            {tabs.map((tab) => (
                                <motion.button
                                    key={tab.id}
                                    className={`flex-shrink-0 py-2 px-4 rounded-full font-semibold text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-purple-100 focus:ring-purple-500 transition-all duration-300 whitespace-nowrap ${tab.disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                                    onClick={() => setActiveTab(tab.id)}
                                    disabled={tab.disabled}
                                    variants={tabVariants}
                                    animate={activeTab === tab.id ? 'active' : 'inactive'}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    {tab.icon}
                                    {tab.label}
                                </motion.button>
                            ))}
                        </div>


                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeTab}
                                variants={contentVariants}
                                initial="hidden"
                                animate="visible"
                                exit="hidden"
                                className={`${activeTab === 'document' ? '' : 'bg-white shadow-lg'} rounded-2xl p-6`}
                            >
                                {
                                    !state && (
                                        <p className="text-gray-400 text-center">{documentT("no_results")}.</p>
                                    )
                                }
                                {
                                    state?.status === 'PROCESSING' && (
                                        <p className="text-gray-400 text-center">{
                                            documentT("waiting_results")
                                        }...</p>
                                    )
                                }
                                <ScrollArea className="h-[60vh]">
                                    {renderContent(state)}
                                </ScrollArea>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                );
            }}

        </ModalComponent>
    );
};